import React from "react";
import { useTranslation } from "react-i18next";
import { useAnalyticsConsent } from "@/lib/analytics";

const buttonClass = "min-h-11 min-w-11 border-2 border-[var(--text)] bg-[var(--surface-container)] px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-[var(--text)] transition-colors duration-100 hover:bg-[var(--surface-container-highest)] focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[var(--surface-container)]";

export function AnalyticsConsentBanner() {
    const { t } = useTranslation();
    const { available, choice, storageError, analyticsError, privacySignal, accept, decline } = useAnalyticsConsent();

    if (!available || (choice !== "undecided" && !analyticsError)) return null;

    return (
        <section
            aria-label={t("analytics.bannerLabel")}
            className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-h-[calc(100dvh-1.5rem)] max-w-3xl overflow-y-auto border-2 border-[var(--text)] bg-[var(--surface-container)] p-5 text-[var(--text)] shadow-brutal md:p-6"
        >
            <h2 className="font-display text-xl uppercase">{t("analytics.title")}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">{t("analytics.description")}</p>
            {storageError && (
                <p role="alert" className="mt-3 border-l-4 border-[var(--text)] pl-3 font-mono text-xs">
                    {t("analytics.storageError")}
                </p>
            )}
            {privacySignal && (
                <p
                    id="analytics-banner-privacy-signal"
                    role="alert"
                    className="mt-3 border-l-4 border-[var(--text)] pl-3 font-mono text-xs"
                >
                    {t("analytics.privacySignal")}
                </p>
            )}
            {analyticsError && (
                <p role="alert" className="mt-3 border-l-4 border-[var(--text)] pl-3 font-mono text-xs">
                    {t("analytics.initializationError")}
                </p>
            )}
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button type="button" onClick={decline} className={buttonClass}>
                    {t("analytics.decline")}
                </button>
                <button
                    type="button"
                    onClick={accept}
                    disabled={privacySignal}
                    aria-describedby={privacySignal ? "analytics-banner-privacy-signal" : undefined}
                    className={buttonClass}
                >
                    {t("analytics.accept")}
                </button>
            </div>
        </section>
    );
}

export function AnalyticsPrivacyControls() {
    const { t } = useTranslation();
    const { available, choice, storageError, analyticsError, privacySignal, accept, decline } = useAnalyticsConsent();

    return (
        <section className="mt-8 border-2 border-[var(--text)] bg-[var(--surface-container)] p-5 text-[var(--text)]" aria-labelledby="analytics-preferences-title">
            <h2 id="analytics-preferences-title" className="font-display text-xl uppercase">
                {t("analytics.preferencesTitle")}
            </h2>
            <p className="mt-2 text-sm text-[var(--text-muted)]" role="status" aria-live="polite">
                {available ? t(`analytics.status.${choice}`) : t("analytics.status.unavailable")}
            </p>
            {storageError && (
                <p role="alert" className="mt-3 font-mono text-xs">
                    {t("analytics.storageError")}
                </p>
            )}
            {privacySignal && (
                <p
                    id="analytics-controls-privacy-signal"
                    role="alert"
                    className="mt-3 font-mono text-xs"
                >
                    {t("analytics.privacySignal")}
                </p>
            )}
            {analyticsError && (
                <p role="alert" className="mt-3 font-mono text-xs">
                    {t("analytics.initializationError")}
                </p>
            )}
            {available && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button type="button" onClick={decline} className={buttonClass}>
                        {t("analytics.decline")}
                    </button>
                    <button
                        type="button"
                        onClick={accept}
                        disabled={privacySignal}
                        aria-describedby={privacySignal ? "analytics-controls-privacy-signal" : undefined}
                        className={buttonClass}
                    >
                        {t("analytics.accept")}
                    </button>
                </div>
            )}
        </section>
    );
}
